import { useState } from 'react';

// Types for the calculator
export interface FourWDLoanInputs {
  vehiclePrice: number;          // Base vehicle price
  downPayment: number;           // Initial deposit
  offRoadPackage: number;        // Cost of 4WD specific additions
  accessories: number;           // Additional accessories cost
  loanTerm: number;              // Loan term in months
  interestRate: number;          // Annual interest rate (%)
  balloonPayment: number;        // Optional balloon payment at end
  applicationFee: number;        // Loan application fee
  annualFee: number;             // Annual loan maintenance fee
}

export interface AmortizationRow {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
  fees: number;
}

export interface FourWDLoanResult {
  monthlyPayment: number;
  totalInterest: number;
  totalFees: number;
  totalCost: number;
  loanAmount: number;
  amortizationSchedule: AmortizationRow[];
  maintenanceEstimate: MaintenanceEstimate;
}

export interface MaintenanceEstimate {
  yearlyMaintenance: number;
  offRoadServiceCost: number;
  tiresReplacement: number;
  suspensionService: number;
  recoveryGear: number;
  totalFiveYearCost: number;
}

// Maintenance cost constants for 4WD vehicles
const MAINTENANCE_RATES = {
  smallSUV: {
    baseYearly: 800,
    offRoadService: 350,
    tires: 1200,
    suspension: 600,
    recoveryGear: 800
  },
  mediumSUV: {
    baseYearly: 1000,
    offRoadService: 450,
    tires: 1600,
    suspension: 900,
    recoveryGear: 1000
  },
  largeSUV: {
    baseYearly: 1200,
    offRoadService: 550,
    tires: 2000,
    suspension: 1200,
    recoveryGear: 1200
  },
  ute: {
    baseYearly: 900,
    offRoadService: 500,
    tires: 1800,
    suspension: 1100,
    recoveryGear: 1000
  }
};

// Calculate the 4WD loan details
export function calculate4WDLoan(inputs: FourWDLoanInputs, vehicleType: keyof typeof MAINTENANCE_RATES = 'mediumSUV'): FourWDLoanResult {
  // Calculate total loan amount including off-road package and accessories
  const loanAmount = 
    (inputs.vehiclePrice + inputs.offRoadPackage + inputs.accessories - inputs.downPayment) + 
    inputs.applicationFee;
  
  // Calculate monthly interest rate
  const monthlyRate = inputs.interestRate / 100 / 12;
  
  // Adjusted loan amount accounting for balloon payment
  const adjustedLoanAmount = loanAmount - (inputs.balloonPayment / Math.pow(1 + monthlyRate, inputs.loanTerm));
  
  // Calculate monthly payment
  let monthlyPayment = 0;
  if (monthlyRate === 0) {
    monthlyPayment = adjustedLoanAmount / inputs.loanTerm;
  } else {
    monthlyPayment = adjustedLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, inputs.loanTerm) 
                     / (Math.pow(1 + monthlyRate, inputs.loanTerm) - 1);
  }

  // Add monthly portion of annual fee
  monthlyPayment += inputs.annualFee / 12;
  
  // Generate amortization schedule
  const amortizationSchedule = generateAmortizationSchedule(
    loanAmount,
    monthlyRate,
    inputs.loanTerm,
    monthlyPayment - (inputs.annualFee / 12), // Remove fee component for amortization
    inputs.balloonPayment,
    inputs.annualFee
  );
  
  // Calculate total interest
  const totalInterest = amortizationSchedule.reduce((sum, row) => sum + row.interest, 0);
  
  // Calculate total fees
  const totalFees = inputs.applicationFee + (inputs.annualFee * Math.ceil(inputs.loanTerm / 12));
  
  // Calculate total cost
  const totalCost = loanAmount + totalInterest + totalFees;
  
  // Calculate maintenance estimates
  const maintenanceEstimate = calculateMaintenanceEstimate(vehicleType, inputs.offRoadPackage);
  
  return {
    monthlyPayment,
    totalInterest,
    totalFees,
    totalCost,
    loanAmount,
    amortizationSchedule,
    maintenanceEstimate
  };
}

// Generate amortization schedule
function generateAmortizationSchedule(
  principal: number,
  monthlyRate: number,
  totalPeriods: number,
  monthlyPayment: number,
  balloonPayment: number,
  annualFee: number
): AmortizationRow[] {
  let balance = principal;
  const schedule: AmortizationRow[] = [];
  
  for (let month = 1; month <= totalPeriods; month++) {
    // Handle last payment with balloon
    if (month === totalPeriods && balloonPayment > 0) {
      const interest = balance * monthlyRate;
      const principalPayment = balance - balloonPayment;
      balance = balloonPayment;
      
      schedule.push({
        month,
        payment: principalPayment + interest,
        principal: principalPayment,
        interest,
        balance,
        fees: month % 12 === 0 ? annualFee : 0
      });
      
      // Add balloon payment as final payment
      schedule.push({
        month: month + 1,
        payment: balloonPayment,
        principal: balloonPayment,
        interest: 0,
        balance: 0,
        fees: 0
      });
      
      break;
    }
    
    const interest = balance * monthlyRate;
    let principalPayment = monthlyPayment - interest;
    
    // Adjust for final payment to ensure balance reaches zero
    if (month === totalPeriods) {
      principalPayment = balance;
    }
    
    balance -= principalPayment;
    
    // Handle annual fee
    const monthlyFee = month % 12 === 0 ? annualFee : 0;
    
    schedule.push({
      month,
      payment: principalPayment + interest,
      principal: principalPayment,
      interest,
      balance: Math.max(0, balance),
      fees: monthlyFee
    });
  }
  
  return schedule;
}

// Calculate maintenance estimates for 4WD vehicles
function calculateMaintenanceEstimate(
  vehicleType: keyof typeof MAINTENANCE_RATES,
  offRoadPackageValue: number
): MaintenanceEstimate {
  const rates = MAINTENANCE_RATES[vehicleType];
  
  // Higher off-road package = more expensive vehicle = higher maintenance
  const offRoadFactor = Math.min(1.5, Math.max(1, 1 + (offRoadPackageValue / 10000) * 0.5));
  
  const yearlyMaintenance = rates.baseYearly * offRoadFactor;
  const offRoadServiceCost = rates.offRoadService * offRoadFactor;
  const tiresReplacement = rates.tires * offRoadFactor;
  const suspensionService = rates.suspension * offRoadFactor;
  const recoveryGear = rates.recoveryGear;
  
  // Calculate 5-year ownership cost estimates
  const totalFiveYearCost = 
    (yearlyMaintenance * 5) + 
    (offRoadServiceCost * 5) + 
    (tiresReplacement * 1.5) + // Assume 1.5 tire replacements in 5 years
    (suspensionService * 2) +  // Assume 2 suspension services in 5 years
    recoveryGear;              // One-time cost
  
  return {
    yearlyMaintenance,
    offRoadServiceCost,
    tiresReplacement,
    suspensionService,
    recoveryGear,
    totalFiveYearCost
  };
}

// Format currency
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', { 
    style: 'currency', 
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 
  }).format(value);
}

// Calculate comparison rate
export function calculateComparisonRate(
  loanAmount: number,
  interestRate: number,
  loanTerm: number,
  applicationFee: number,
  annualFee: number
): number {
  // Simplified comparison rate calculation
  const totalFees = applicationFee + (annualFee * (loanTerm / 12));
  const effectiveInterest = interestRate + ((totalFees / loanAmount) * 100);
  
  return Math.round(effectiveInterest * 100) / 100; // Round to 2 decimal places
}

// Get vehicle size based on price
export function getVehicleSizeByPrice(price: number): keyof typeof MAINTENANCE_RATES {
  if (price < 40000) return 'smallSUV';
  if (price < 60000) return 'mediumSUV';
  if (price < 80000) return 'ute';
  return 'largeSUV';
}

// Calculate fuel cost estimates
export function calculateFuelCosts(
  vehicleType: keyof typeof MAINTENANCE_RATES,
  kmPerYear: number = 15000,
  fuelPrice: number = 1.8 // Average price per liter
): number {
  const fuelEfficiency = {
    smallSUV: 8.5,    // L/100km
    mediumSUV: 10.5,  // L/100km
    largeSUV: 13.0,   // L/100km
    ute: 11.0         // L/100km
  };
  
  const litersPerYear = (kmPerYear / 100) * fuelEfficiency[vehicleType];
  return litersPerYear * fuelPrice;
} 